// src/pages/supplier/ManageSuppliersPage.jsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Package,
  Plus,
  Edit,
  Trash2,
  AlertCircle,
  MapPin,
  Globe,
} from "lucide-react";
import {
  PageHeader,
  StatsCards,
  DataTable,
  FilterPanel,
} from "../../components/common";
import { useLocalization } from "../../contexts/LocalizationContext";
import { useAlert } from "../../contexts/AlertContext";
import {
  getAllSuppliers,
  getSupplierStatistics,
  deleteSupplier,
} from "../../services/api/supplier";
import SupplierModal from "../../components/common/SupplierModal/SupplierModal";
import styles from "./ManageSuppliersPage.module.css";

const ManageSuppliersPage = () => {
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();

  // State
  const [suppliers, setSuppliers] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingStats, setIsLoadingStats] = useState(true);

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    total: 0,
    pageSizeOptions: [10, 20, 50, 100],
  });

  // Sorting state
  const [sorting, setSorting] = useState({
    orderBy: "name",
    orderDirection: "ASC",
  });

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    isActive: "",
    city: "",
    country: "",
  });

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  // Delete confirmation
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load data
  useEffect(() => {
    loadSuppliers();
  }, [
    pagination.currentPage,
    pagination.pageSize,
    sorting.orderBy,
    sorting.orderDirection,
    filters,
  ]);

  useEffect(() => {
    loadStatistics();
  }, []);

  const loadSuppliers = async () => {
    try {
      setIsLoading(true);
      const params = {
        page: pagination.currentPage,
        limit: pagination.pageSize,
        search: filters.search,
        orderBy: sorting.orderBy,
        orderDirection: sorting.orderDirection,
      };

      if (filters.isActive !== "") params.isActive = filters.isActive;
      if (filters.city) params.city = filters.city;
      if (filters.country) params.country = filters.country;

      const response = await getAllSuppliers(params);
      setSuppliers(response.suppliers || []);
      setPagination((prev) => ({
        ...prev,
        total: response.total || 0,
      }));
    } catch (err) {
      console.error("Error loading suppliers:", err);
      showError(
        err.response?.data?.message || t("supplier.failedToLoadSuppliers")
      );
    } finally {
      setIsLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      setIsLoadingStats(true);
      const stats = await getSupplierStatistics();
      setStatistics(stats);
    } catch (err) {
      console.error("Error loading statistics:", err);
    } finally {
      setIsLoadingStats(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleSearchChange = (searchValue) => {
    setFilters((prev) => ({ ...prev, search: searchValue }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: newPageSize,
      currentPage: 1,
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: "",
      isActive: "",
      city: "",
      country: "",
    });
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const hasActiveFilters = () => {
    return Object.values(filters).some((value) => value !== "");
  };

  const handleCreateSupplier = () => {
    setModalMode("create");
    setSelectedSupplier(null);
    setShowModal(true);
  };

  const handleEditSupplier = (supplier) => {
    setModalMode("edit");
    setSelectedSupplier(supplier);
    setShowModal(true);
  };

  const handleDeleteSupplier = (supplier) => {
    setSupplierToDelete(supplier);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!supplierToDelete) return;

    try {
      setIsDeleting(true);
      const response = await deleteSupplier(supplierToDelete.id);

      if (response.deleted) {
        success(t("supplier.supplierDeletedSuccessfully"));
      } else {
        success(response.message || t("supplier.supplierMarkedAsInactive"));
      }

      setShowDeleteDialog(false);
      setSupplierToDelete(null);
      loadSuppliers();
      loadStatistics();
    } catch (err) {
      console.error("Error deleting supplier:", err);
      showError(
        err.response?.data?.message || t("supplier.failedToDeleteSupplier")
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handleModalSuccess = () => {
    setShowModal(false);
    setSelectedSupplier(null);
    loadSuppliers();
    loadStatistics();
  };

  const handleExport = () => {
    const headers = [
      t("supplier.supplierName"),
      t("supplier.contactPerson"),
      t("supplier.email"),
      t("supplier.phone"),
      t("supplier.city"),
      t("supplier.country"),
      t("supplier.paymentTerms"),
      t("common.status"),
    ];

    const rows = suppliers.map((supplier) => [
      supplier.name,
      supplier.contactPerson || "-",
      supplier.email || "-",
      supplier.phone || "-",
      supplier.city || "-",
      supplier.country || "-",
      supplier.paymentTerms || "-",
      supplier.isActive ? t("common.active") : t("common.inactive"),
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `suppliers-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Stats configuration
  const statsConfig = [
    {
      key: "total",
      label: t("supplier.totalSuppliers"),
      value: statistics?.totalSuppliers || 0,
      icon: Package,
      color: "#7F5CFF",
      loading: isLoadingStats,
    },
    {
      key: "active",
      label: t("supplier.activeSuppliers"),
      value: statistics?.activeSuppliers || 0,
      icon: Package,
      color: "#10b981",
      loading: isLoadingStats,
    },
    {
      key: "inactive",
      label: t("supplier.inactiveSuppliers"),
      value: statistics?.inactiveSuppliers || 0,
      icon: Package,
      color: "#6b7280",
      loading: isLoadingStats,
    },
  ];

  // Filter definitions
  const filterDefinitions = [
    {
      key: "isActive",
      label: t("common.status"),
      type: "select",
      placeholder: t("supplier.allStatuses"),
      options: [
        { value: "true", label: t("common.active") },
        { value: "false", label: t("common.inactive") },
      ],
    },
    {
      key: "city",
      label: t("supplier.city"),
      type: "text",
      icon: <MapPin size={18} />,
      placeholder: t("supplier.filterByCity"),
    },
    {
      key: "country",
      label: t("supplier.country"),
      type: "text",
      icon: <Globe size={18} />,
      placeholder: t("supplier.filterByCountry"),
    },
  ];

  // Table columns
  const columns = [
    {
      key: "name",
      header: t("supplier.supplierName"),
      sortable: true,
      width: "200px",
      render: (row) => <div className={styles.supplierName}>{row.name}</div>,
    },
    {
      key: "contactPerson",
      header: t("supplier.contactPerson"),
      width: "150px",
      render: (row) => (
        <div className={styles.contactPerson}>
          {row.contactPerson || t("common.na")}
        </div>
      ),
    },
    {
      key: "email",
      header: t("supplier.email"),
      width: "180px",
      render: (row) => (
        <div className={styles.email}>{row.email || t("common.na")}</div>
      ),
    },
    {
      key: "phone",
      header: t("supplier.phone"),
      width: "130px",
      render: (row) => (
        <div className={styles.phone}>{row.phone || t("common.na")}</div>
      ),
    },
    {
      key: "city",
      header: t("supplier.city"),
      width: "120px",
      render: (row) => (
        <div className={styles.city}>{row.city || t("common.na")}</div>
      ),
    },
    {
      key: "country",
      header: t("supplier.country"),
      width: "120px",
      render: (row) => (
        <div className={styles.country}>{row.country || t("common.na")}</div>
      ),
    },
    {
      key: "paymentTerms",
      header: t("supplier.paymentTerms"),
      width: "130px",
      render: (row) => (
        <div className={styles.paymentTerms}>
          {row.paymentTerms || t("common.na")}
        </div>
      ),
    },
    {
      key: "isActive",
      header: t("common.status"),
      width: "100px",
      render: (row) => (
        <span
          className={`${styles.statusBadge} ${
            row.isActive ? styles.active : styles.inactive
          }`}
        >
          {row.isActive ? t("common.active") : t("common.inactive")}
        </span>
      ),
    },
  ];

  // Table actions
  const tableActions = {
    render: (row) => (
      <div className={styles.actionButtons}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleEditSupplier(row);
          }}
          className={styles.actionButton}
          title={t("common.edit")}
        >
          <Edit size={16} />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteSupplier(row);
          }}
          className={`${styles.actionButton} ${styles.deleteAction}`}
          title={t("common.delete")}
        >
          <Trash2 size={16} />
        </button>
      </div>
    ),
    width: "100px",
    frozen: true,
  };

  return (
    <div className={styles.manageSuppliersPage}>
      {/* Page Header */}
      <PageHeader
        title={t("supplier.manageSuppliers")}
        description={t("supplier.viewAndManageSuppliers")}
        icon={Package}
        actions={
          <button
            onClick={handleCreateSupplier}
            className={styles.primaryButton}
          >
            <Plus size={20} />
            {t("supplier.createNewSupplier")}
          </button>
        }
      />

      {/* Statistics Cards */}
      <StatsCards stats={statsConfig} />

      {/* Search and Filters */}
      <FilterPanel
        filters={filterDefinitions}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
        show={showFilters}
        onToggle={() => setShowFilters(!showFilters)}
        onExport={handleExport}
        showExport={true}
        exportButtonText={t("common.export")}
        searchConfig={{
          show: true,
          value: filters.search,
          onChange: handleSearchChange,
          placeholder: t("supplier.searchSuppliers"),
        }}
        pageSizeConfig={{
          show: true,
          value: pagination.pageSize,
          onChange: handlePageSizeChange,
          options: pagination.pageSizeOptions,
        }}
      />

      {/* Data Table */}
      <DataTable
        data={suppliers}
        columns={columns}
        actions={tableActions}
        onRowClick={(row) => handleEditSupplier(row)}
        sorting={sorting}
        onSortChange={setSorting}
        pagination={pagination}
        onPaginationChange={setPagination}
        loading={isLoading}
        emptyMessage={
          hasActiveFilters()
            ? t("supplier.noSuppliersFound")
            : t("supplier.noSuppliersYet")
        }
        emptyDescription={
          hasActiveFilters()
            ? t("supplier.noSuppliersFoundDescription")
            : t("supplier.noSuppliersYetDescription")
        }
        emptyIcon={<Package size={64} />}
        emptyAction={
          hasActiveFilters() ? (
            <button
              onClick={handleClearFilters}
              className={styles.primaryButton}
            >
              {t("common.clearFilters")}
            </button>
          ) : (
            <button
              onClick={handleCreateSupplier}
              className={styles.primaryButton}
            >
              <Plus size={18} />
              {t("supplier.createFirstSupplier")}
            </button>
          )
        }
      />

      {/* Supplier Modal */}
      {showModal && (
        <SupplierModal
          mode={modalMode}
          supplier={selectedSupplier}
          onClose={() => {
            setShowModal(false);
            setSelectedSupplier(null);
          }}
          onSuccess={handleModalSuccess}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && supplierToDelete && (
        <div className={styles.dialogOverlay}>
          <div className={styles.dialogContent}>
            <div className={styles.dialogHeader}>
              <AlertCircle className={styles.dialogIcon} size={24} />
              <h3>{t("supplier.deleteSupplier")}</h3>
            </div>
            <div className={styles.dialogBody}>
              <p>
                {t("supplier.confirmDelete", { name: supplierToDelete.name })}
              </p>
              <div className={styles.warningMessage}>
                <AlertCircle size={16} />
                <span>{t("supplier.deleteWarning")}</span>
              </div>
            </div>
            <div className={styles.dialogActions}>
              <button
                onClick={() => {
                  setShowDeleteDialog(false);
                  setSupplierToDelete(null);
                }}
                className={styles.dialogCancelButton}
                disabled={isDeleting}
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={confirmDelete}
                className={styles.dialogDeleteButton}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <div className={styles.buttonSpinner}></div>
                    {t("common.deleting")}
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    {t("common.delete")}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageSuppliersPage;
